import { type FormEvent } from "react";

import { PaperPlaneTilt } from "@phosphor-icons/react";
import { cn } from "../utils/cn";

interface MessageComposerProps {
  value: string;
  onChange: (value: string) => void;
  onSend: () => void | Promise<void>;
  placeholder: string;
  label: string;
  sendLabel: string;
  inputId: string;
  isSending?: boolean;
  disabled?: boolean;
  className?: string;
}

export function MessageComposer({
  value,
  onChange,
  onSend,
  placeholder,
  label,
  sendLabel,
  inputId,
  isSending = false,
  disabled = false,
  className,
}: MessageComposerProps) {
  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (disabled || !value.trim()) return;
    void onSend();
  };

  return (
    <form
      onSubmit={handleSubmit}
      className={cn(
        "flex items-end gap-3 rounded-xl bg-white px-3 py-2 shadow-sm ring-1 ring-slate-200 dark:bg-slate-900 dark:ring-slate-800",
        className
      )}
    >
      <label className="sr-only" htmlFor={inputId}>
        {label}
      </label>
      <textarea
        id={inputId}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            if (disabled || !value.trim()) return;
            void onSend();
          }
        }}
        className="pm-scrollbar min-h-[72px] flex-1 resize-none rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 shadow-inner placeholder:text-slate-400 focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:placeholder:text-slate-500"
        placeholder={placeholder}
      />
      <button
        type="submit"
        disabled={disabled || isSending || !value.trim()}
        className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900 disabled:cursor-not-allowed disabled:bg-slate-400 disabled:text-slate-200 dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-slate-200 dark:disabled:bg-slate-700 dark:disabled:text-slate-400"
      >
        <PaperPlaneTilt size={16} weight="bold" aria-hidden />
        {sendLabel}
      </button>
    </form>
  );
}
